import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../config";
import { AuditTrailForExport } from "../util/getAuditTrailData";

const baseUrl = BASE_URL + "project";

// 📌 Create Project (POST)
export const createProject = createAsyncThunk(
  "projectId/create",
  async (projectData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${baseUrl}/create`, projectData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error creating project");
    }
  }
);

// 📌 Update Project (PUT)
export const updateProject = createAsyncThunk(
  "projectId/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${baseUrl}/update/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error updating project");
    }
  }
);

// 📌 Get Project by ID (GET)
export const getProjectById = createAsyncThunk(
  "projectId/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/get-by-id/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching project");
    }
  }
);

// 📌 Get All Projects (GET)
export const getAllProjects = createAsyncThunk(
  "projectId/getAll",
  async ({ searchQuery }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-all?${searchQuery}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching projects");
    }
  }
);

// 📌 Delete Project (DELETE)
export const deleteProject = createAsyncThunk(
  "projectId/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${baseUrl}/delete/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error deleting project");
    }
  }
);
export const exportCSV = createAsyncThunk(
  "type/exportCSV",
  async (_, { rejectWithValue }) => {
    try {
      await AuditTrailForExport({
        apiUrl:`${baseUrl}/export-project`,
        file_name:"projectid-data-"+Date.now()+".csv",
        action:"EXPORT_PARTS_DATA",
        action_to_show:"Project Id Table data exported as CSV"
      })      
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error exporting CSV");
    }
  }
);
 
export const uploadCSV = createAsyncThunk(
  "type/uploadCSV",
  async ({ csvFile, auditTrailData }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", csvFile);
      formData.append("audit_trail_data", JSON.stringify(auditTrailData));
 
      const response = await axios.post(`${baseUrl}/import-project`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error uploading CSV");
    }
  }
); 
// 🛠 ProjectId Slice
const projectIdSlice = createSlice({
  name: "projectId",
  initialState: {
    projects: [],
    project: null,
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createProject.pending, (state) => {
        state.loading = true;
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.loading = false;
        state.projects.push(action.payload);
      })
      .addCase(createProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
      })
      .addCase(updateProject.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = state.projects.map((project) =>
          project.id === action.payload.id ? action.payload : project
        );
      })
      .addCase(updateProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
      })
      .addCase(getProjectById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProjectById.fulfilled, (state, action) => {
        state.loading = false;
        state.project = action.payload;
      })
      .addCase(getProjectById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
      })
      .addCase(getAllProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = action.payload.results;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getAllProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
      })
      .addCase(deleteProject.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = state.projects.filter(
          (project) => project.id !== action.payload
        );
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
      })
            .addCase(exportCSV.pending, (state) => {
              state.loading = true;
            })
            .addCase(exportCSV.fulfilled, (state) => {
              state.loading = false;
            })
            .addCase(exportCSV.rejected, (state, action) => {
              state.loading = false;
              state.error =
                action.payload.message || action.payload.error || "An error occured";
            })

            // Upload CSV
            .addCase(uploadCSV.pending, (state) => {
              state.loading = true;
              state.uploadError = null;
            })
            .addCase(uploadCSV.fulfilled, (state) => {
              state.loading = false;
              state.uploadError = null;
            })
            .addCase(uploadCSV.rejected, (state, action) => {
              state.loading = false;
              state.uploadError =
                action.payload.message || action.payload.error || "An error occured";
            });
  },
});

export default projectIdSlice.reducer;
